import { Amplify } from 'aws-amplify';
import {
  fetchAuthSession,
  getCurrentUser,
  signInWithRedirect,
  signOut,
  type AuthUser,
} from 'aws-amplify/auth';
import { useEffect, useState } from 'react';

import { env, isCognitoConfigured } from './env';

let configured = false;

export function configureAuth(): void {
  if (configured || !isCognitoConfigured()) return;
  Amplify.configure({
    Auth: {
      Cognito: {
        userPoolId: env.cognito.userPoolId!,
        userPoolClientId: env.cognito.userPoolClientId!,
        loginWith: {
          oauth: {
            domain: env.cognito.domain!,
            scopes: ['openid', 'email', 'profile'],
            redirectSignIn: [env.cognito.redirectSignIn!],
            redirectSignOut: [env.cognito.redirectSignOut!],
            responseType: 'code',
          },
        },
      },
    },
  });
  configured = true;
}

export async function startLogin(): Promise<void> {
  configureAuth();
  if (!isCognitoConfigured()) {
    throw new Error(
      'Cognito Hosted UI is not configured. Set the VITE_COGNITO_* env vars to enable sign-in.',
    );
  }
  await signInWithRedirect();
}

export async function logout(): Promise<void> {
  configureAuth();
  await signOut();
}

export async function getAccessToken(): Promise<string | undefined> {
  configureAuth();
  if (!isCognitoConfigured()) return undefined;
  try {
    const session = await fetchAuthSession();
    return session.tokens?.accessToken?.toString();
  } catch {
    return undefined;
  }
}

export interface CurrentUser {
  userId: string;
  username: string;
  displayName?: string;
  groups: string[];
}

export function useCurrentUser(): { user: CurrentUser | null; loading: boolean } {
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [loading, setLoading] = useState(isCognitoConfigured());

  useEffect(() => {
    if (!isCognitoConfigured()) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    configureAuth();
    getCurrentUser()
      .then(async (aws: AuthUser) => {
        if (cancelled) return;
        let displayName: string | undefined;
        let groups: string[] = [];
        try {
          const session = await fetchAuthSession();
          const payload = session.tokens?.idToken?.payload;
          const name = payload?.['name'];
          const preferredUsername = payload?.['preferred_username'];
          const email = payload?.['email'];
          displayName =
            (typeof name === 'string' && name ? name : undefined) ??
            (typeof preferredUsername === 'string' && preferredUsername
              ? preferredUsername
              : undefined) ??
            (typeof email === 'string' && email ? email : undefined);
          const rawGroups = payload?.['cognito:groups'];
          groups = Array.isArray(rawGroups) ? (rawGroups as string[]) : [];
        } catch {
          // Non-critical; fall back to username
        }
        if (cancelled) return;
        setUser({ userId: aws.userId, username: aws.username, displayName, groups });
      })
      .catch(() => {
        if (cancelled) return;
        setUser(null);
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return { user, loading };
}
